import { UserRole } from '../types/enums.js';
// ─── Page access ────────────────────────────────────────────────────────────
// Admin: every page.
// Manager: every page except Users + Settings.
// Worker: only the pages they need for self-service.
// Per Vic/Graham's brief, a Worker only sees their dashboard (with cert summary
// + availability buttons), their deployment records, their availability
// calendar, plus the site-tool pages they need to operate on site (sign-in
// and document sign). Their profile + cert summary are surfaced via the
// dashboard rather than a stand-alone Workers / Training page.
const WORKER_VISIBLE_PAGES = new Set([
    'dashboard',
    'deploylog', // restricted to own deployments inside the page
    'calendar', // restricted to own row inside the page
    'signin',
    'docsign',
]);
const SUPERVISOR_VISIBLE_PAGES = new Set([
    'dashboard',
    'workers',
    'deploylog',
    'calendar',
    'signin',
    'docsign',
]);
const MANAGER_FORBIDDEN_PAGES = new Set([
    'settings',
    'settings.users',
    // Scheduled Reports configures automated dispatching — treated as a system
    // settings page even though it's not literally under /settings.
    'scheduledreports',
    'bulkdeploy',
    'qrscan',
]);
export function canViewPage(user, page) {
    if (!user.active)
        return false;
    switch (user.role) {
        case UserRole.Admin:
            return true;
        case UserRole.Manager:
            return !MANAGER_FORBIDDEN_PAGES.has(page);
        case UserRole.Supervisor:
            return SUPERVISOR_VISIBLE_PAGES.has(page);
        case UserRole.Worker:
            return WORKER_VISIBLE_PAGES.has(page);
        default:
            return false;
    }
}
// ─── Project ────────────────────────────────────────────────────────────────
export function canViewProject(user, _project) {
    if (!user.active)
        return false;
    if (user.role === UserRole.Admin || user.role === UserRole.Manager)
        return true;
    // Workers can't open project records directly.
    return false;
}
export function canEditProject(user, _project) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin;
}
// ─── Worker ─────────────────────────────────────────────────────────────────
export function canViewWorker(user, worker) {
    if (!user.active)
        return false;
    if (user.role === UserRole.Admin || user.role === UserRole.Manager)
        return true;
    if (user.role === UserRole.Worker)
        return worker.selfUserId === user.id;
    return false;
}
export function canEditWorker(user, worker) {
    if (!user.active)
        return false;
    if (user.role === UserRole.Admin)
        return true;
    // Workers can flip their own availability via the dedicated signal endpoint,
    // not via this generic edit predicate.
    if (user.role === UserRole.Worker)
        return worker.selfUserId === user.id;
    return false;
}
// Fields hidden from a worker viewing their own profile. The brief calls out
// the rating explicitly; admin-authored notes are filtered at the WorkerNote
// query layer (not a single field on Worker). DNU translation also happens
// outside this list — it's a value-level concern, not a field-level one.
const WORKER_SELF_HIDDEN_FIELDS = new Set(['rating']);
export function canViewWorkerField(user, _worker, field) {
    if (!user.active)
        return false;
    if (user.role === UserRole.Admin || user.role === UserRole.Manager)
        return true;
    if (user.role === UserRole.Worker)
        return !WORKER_SELF_HIDDEN_FIELDS.has(field);
    return false;
}
// ─── Deployment ─────────────────────────────────────────────────────────────
export function canViewDeployment(user, deploy, _project, worker) {
    if (!user.active)
        return false;
    if (user.role === UserRole.Admin || user.role === UserRole.Manager)
        return true;
    if (user.role === UserRole.Worker) {
        // Worker can only see their own deployment rows.
        if (user.workerId && deploy.workerId === user.workerId)
            return true;
        if (worker && worker.selfUserId === user.id)
            return true;
        return false;
    }
    return false;
}
export function canEditDeployment(user, _deploy, _project) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin;
}
// ─── Contact ────────────────────────────────────────────────────────────────
export function canViewContact(user, _contact) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin || user.role === UserRole.Manager;
}
export function canEditContact(user, _contact) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin;
}
// ─── Supplier ───────────────────────────────────────────────────────────────
export function canViewSupplier(user, _supplier) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin || user.role === UserRole.Manager;
}
// ─── Vacancy ────────────────────────────────────────────────────────────────
export function canCreateVacancy(user) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin;
}
export function canViewVacancy(user, _vacancy, _project) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin || user.role === UserRole.Manager;
}
// ─── Comms (WhatsApp / email / SMS broadcasts) ──────────────────────────────
// Managers explicitly need the comms buttons per Vic/Graham's brief.
export function canSendBroadcast(user) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin || user.role === UserRole.Manager;
}
// ─── Availability ───────────────────────────────────────────────────────────
export function canSignAvailability(user, worker) {
    if (!user.active)
        return false;
    if (user.role === UserRole.Admin)
        return true;
    if (user.role === UserRole.Worker)
        return worker.selfUserId === user.id;
    return false;
}
export function canViewAvailabilitySignal(user, _signal, worker) {
    if (!user.active)
        return false;
    if (user.role === UserRole.Admin || user.role === UserRole.Manager)
        return true;
    if (user.role === UserRole.Worker && worker)
        return worker.selfUserId === user.id;
    return false;
}
// ─── System ─────────────────────────────────────────────────────────────────
// Admin-only across the board: users, settings, audit, exports, imports.
export function canManageSettings(user) {
    return user.active && user.role === UserRole.Admin;
}
export function canManageUsers(user) {
    return user.active && user.role === UserRole.Admin;
}
export function canViewAudit(user) {
    return user.active && user.role === UserRole.Admin;
}
export function canExportData(user) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin || user.role === UserRole.Manager;
}
export function canImportData(user) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin;
}
// ─── Compliance ─────────────────────────────────────────────────────────────
export function canViewCompliance(user) {
    if (!user.active)
        return false;
    return user.role === UserRole.Admin || user.role === UserRole.Manager;
}
//# sourceMappingURL=index.js.map